// utils/httpClient.ts
import { authOptions } from '@/lib/auth';
import {
  ApiErrorResponse,
  ApiResponse,
  HttpMethod,
  RequestConfig,
} from '@/types/api';
import { getServerSession, Session } from 'next-auth';
import { getSession } from 'next-auth/react';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL;

// Helper to build query string
const buildQueryString = (params?: Record<string, any>): string => {
  if (!params) return '';

  const validParams = Object.entries(params)
    .filter(([_, value]) => value != null && value !== '')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: String(value) }), {});

  return Object.keys(validParams).length
    ? `?${new URLSearchParams(validParams)}`
    : '';
};

// Main fetch function
export async function fetchApi<T>(
  endpoint: string,
  method: HttpMethod,
  config: RequestConfig = {}
): Promise<ApiResponse<T>> {
  const { headers = {}, body, queryParams } = config;

  let url = `${BASE_URL}${endpoint}${buildQueryString(queryParams)}`;

  if (config.baseURL) {
    url = `${config.baseURL}${endpoint}${buildQueryString(queryParams)}`;
  }

  if (!config.skipAuth) {
    let session: Session | null = null;

    if (process.browser) {
      session = await getSession();
    } else {
      session = await getServerSession(authOptions);
    }

    if (session?.user?.idToken) {
      headers['Authorization'] = `Bearer ${session.user.idToken}`;
    }
  }

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    // if (!response.ok && response.status === 401) {

    // }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[HttpClient] Non-OK response', {
          url,
          status: response.status,
          statusText: response.statusText,
          data,
        });
      }
      const error = new Error(
        data?.message || response.statusText
      ) as ApiErrorResponse;
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return {
      data: data as T,
      status: response.status,
      headers: response.headers,
    };
  } catch (error) {
    console.error(`HTTP request failed: ${error}`);
    throw error;
  }
}

// Helper methods for common HTTP verbs
export const http = {
  fetch: <T>({
    method = 'GET',
    endpoint,
    config,
  }: {
    method?: HttpMethod;
    endpoint: string;
    config?: RequestConfig;
  }) => fetchApi<T>(endpoint, method, config),

  get: <T>(endpoint: string, config?: RequestConfig) =>
    fetchApi<T>(endpoint, 'GET', config),

  post: <T>(endpoint: string, config?: RequestConfig) =>
    fetchApi<T>(endpoint, 'POST', config),

  put: <T>(endpoint: string, config?: RequestConfig) =>
    fetchApi<T>(endpoint, 'PUT', config),

  patch: <T>(endpoint: string, config?: RequestConfig) =>
    fetchApi<T>(endpoint, 'PATCH', config),

  delete: <T>(endpoint: string, config?: RequestConfig) =>
    fetchApi<T>(endpoint, 'DELETE', config),
};
